import { ART_PAL, body, ball, edge, gloss, mix, shade, tint, type ArtPal, type ArtTheme } from './covers/kit';

/**
 * Liftoff rocket — friendly, faces right, drawn in a 128×128 box centred on (64,64).
 * `crashed`: tilted nose-down (35°) with a small puff at the tail — no fire, no explosion (HANDOFF honesty).
 * The flight exhaust is marked `data-flame` so CSS can flicker it (off under reduced motion).
 */
export function rocketMarkup(P: ArtPal, p: string, crashed = false): string {
  const hull = P.dark ? P.ink : tint(P.blue, 0.9);
  const hullEdge = P.dark ? shade(P.ink, 0.42) : shade(P.blue, 0.35);
  const nozzle = P.dark ? mix(P.blue, P.bg, 0.45) : shade(P.blue, 0.15);
  const flameA = P.gold;
  const flameB = P.orange;
  const puff = P.dark ? mix(P.ink, P.bg, 0.3) : mix(P.ink, P.bg, 0.78);

  const defs =
    `<defs>` +
    body(`${p}h`, hull, P.dark ? 0.6 : 0.7, P.dark ? 0.3 : 0.12, 0.25, 1) +
    body(`${p}n`, P.teal, 0.35, 0.3, 0.3, 1) +
    body(`${p}f`, P.orange, 0.3, 0.3, 0.4, 1) +
    body(`${p}z`, nozzle, 0.25, 0.3, 0.2, 1) +
    ball(`${p}w`, P.sky, 0.55, 0.35) +
    body(`${p}r`, P.gold, 0.35, 0.3, 0.3, 1) +
    `<linearGradient id="${p}x" x1="1" y1="0" x2="0" y2="0"><stop offset="0" stop-color="${tint(flameA, 0.5)}"/>` +
    `<stop offset=".35" stop-color="${flameA}"/><stop offset=".8" stop-color="${flameB}" stop-opacity=".75"/><stop offset="1" stop-color="${flameB}" stop-opacity="0"/></linearGradient>` +
    (crashed ? ball(`${p}p`, puff, 0.35, 0.18) : '') +
    `</defs>`;

  const fe = edge(P.orange, P);
  const rocket =
    // exhaust (flight only)
    (crashed
      ? ''
      : `<path data-flame="" d="M22 55C11 55 3 60 0 64C3 68 11 73 22 73Z" fill="url(#${p}x)"/>` +
        `<path d="M22 59C15 59 10 62 8 64C10 66 15 69 22 69Z" fill="${tint(flameA, 0.6)}" fill-opacity=".85"/>`) +
    // fins behind the hull
    `<path d="M30 49L19 29Q17 23 24 25L56 48Z" fill="url(#${p}f)" stroke="${fe}" stroke-width="2" stroke-linejoin="round"/>` +
    `<path d="M30 79L19 99Q17 105 24 103L56 80Z" fill="url(#${p}f)" stroke="${fe}" stroke-width="2" stroke-linejoin="round"/>` +
    // nozzle
    `<rect x="14" y="55" width="12" height="18" rx="3" fill="url(#${p}z)" stroke="${shade(nozzle, 0.3)}" stroke-width="2"/>` +
    // hull
    `<path d="M28 46H76C94 46 108 55 114 64C108 73 94 82 76 82H28Q20 82 20 64Q20 46 28 46Z" fill="url(#${p}h)" stroke="${hullEdge}" stroke-width="2.2" stroke-linejoin="round"/>` +
    // nose cone
    `<path d="M84 47.2C98 49 108.6 56 113.6 64C108.6 72 98 79 84 80.8Q88.5 64 84 47.2Z" fill="url(#${p}n)"/>` +
    // band
    `<path d="M33 47.1H40V80.9H33Z" fill="url(#${p}f)"/>` +
    // porthole
    `<circle cx="63" cy="62" r="10.5" fill="url(#${p}r)" stroke="${edge(P.gold, P)}" stroke-width="1.6"/>` +
    `<circle cx="63" cy="62" r="7" fill="url(#${p}w)"/>` +
    gloss(60.5, 59.2, 3.2, 1.8, -35, 0.85) +
    // hull highlight (light from top-left) + belly fin
    gloss(58, 51.5, 22, 2.6, -2, P.dark ? 0.7 : 0.9) +
    `<path d="M48 64H78" stroke="${hullEdge}" stroke-opacity=".25" stroke-width="2" stroke-linecap="round"/>`;

  if (!crashed) return defs + rocket;

  const puffs =
    `<g opacity=".92">` +
    `<circle cx="30" cy="30" r="12" fill="url(#${p}p)"/>` +
    `<circle cx="17" cy="38" r="9" fill="url(#${p}p)"/>` +
    `<circle cx="42" cy="22" r="8" fill="url(#${p}p)"/>` +
    `<circle cx="10" cy="24" r="5" fill="url(#${p}p)"/>` +
    `</g>`;
  return defs + `<g transform="rotate(35 64 64) translate(4 0) scale(.92)">${rocket}</g>` + puffs;
}

/** Standalone SVG document of the rocket (for the gallery / downloads). */
export function rocketSvg(theme: ArtTheme, crashed = false, prefix = 'rk'): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width="128" height="128">${rocketMarkup(ART_PAL[theme], prefix, crashed)}</svg>`;
}
